import { useMemo } from "react";

import useTypedContext from "hooks/useTypedContext";
import { STACK_MANAGE_ACCESS_TYPES } from "shared/Stack/access/useCanCreateStack";
import useCanDeleteStack from "shared/Stack/access/useCanDeleteStack";
import { Space, Stack } from "types/generated";
import { hasAtLeastOneRequiredAction } from "utils/access";
import { SpacesContext } from "views/Account/SpacesProvider";
import { AccountContext } from "views/AccountWrapper";

type StackMoveSpaces = {
  canMoveStack: boolean;
  spaces: Space[];
};

/**
 * Whether the viewer can move the stack to another space, plus the options for the settings space
 * field. Moving needs the delete permission on the stack (mirroring the backend, which falls back
 * to manage) and at least one other space the viewer can manage stacks in. The stack's own space
 * always heads the options so the field can display it and the viewer can re-select it - staying
 * put is not a move and needs no extra permission.
 */
const useStackMoveSpaces = (stack: Stack): StackMoveSpaces => {
  const { spaces } = useTypedContext(SpacesContext);
  const { viewer } = useTypedContext(AccountContext);
  const canDeleteStack = useCanDeleteStack();

  return useMemo(() => {
    const currentSpace =
      spaces.find((space) => space.id === stack.spaceDetails.id) || stack.spaceDetails;

    const destinationSpaces = spaces.filter(
      (space) =>
        space.id !== currentSpace.id &&
        hasAtLeastOneRequiredAction(STACK_MANAGE_ACCESS_TYPES, viewer, space.id)
    );

    const canMoveStack = canDeleteStack(stack).result && destinationSpaces.length > 0;

    return {
      canMoveStack,
      spaces: canMoveStack ? [currentSpace, ...destinationSpaces] : [currentSpace],
    };
  }, [spaces, viewer, stack, canDeleteStack]);
};

export default useStackMoveSpaces;
